import React, { useState, useEffect } from 'react';
import PRODUCTION_CONFIG from '../config/production.js';

const CampaignTrackingHistory = ({ campaignId, campaignName, onClose, campaign }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Function to get current user from localStorage
  const getCurrentUser = () => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  };

  useEffect(() => {
    loadHistory();
  }, [campaignId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? PRODUCTION_CONFIG.API_BASE_URL 
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      // Obtener historial real de la base de datos
      const response = await fetch(`${API_URL}/campaign-tracking/${campaignId}/history`);
      
      if (response.ok) {
        const historyData = await response.json();
        
        // Crear historial combinado: datos reales de la campaña + historial de la base de datos
        const combinedHistory = [];
        
        if (campaign) {
          // Entrada de creación con datos reales de la campaña
          combinedHistory.push({
            id: `campaign-created-${campaign.id}`,
            action_type: 'created',
            description: `Campaña creada: "${campaign.campana}"`,
            user_name: campaign.agente || 'Sistema',
            created_at: campaign.created_at || new Date().toISOString(),
            campaign_data: {
              campana: campaign.campana,
              asunto: campaign.asunto,
              acciones: campaign.acciones
            }
          });
          
          // Si hay asunto, agregarlo como entrada
          if (campaign.asunto && campaign.asunto.trim()) {
            combinedHistory.push({
              id: `campaign-asunto-${campaign.id}`,
              action_type: 'asunto_defined',
              description: `Asunto definido: "${campaign.asunto}"`,
              user_name: campaign.agente || 'Sistema',
              created_at: campaign.created_at || new Date().toISOString()
            });
          }
          
          // Si hay acciones, agregarlas como entrada
          if (campaign.acciones && campaign.acciones.trim()) {
            combinedHistory.push({
              id: `campaign-acciones-${campaign.id}`,
              action_type: 'acciones_added',
              description: `Acciones tomadas: "${campaign.acciones}"`,
              user_name: campaign.agente || 'Sistema',
              created_at: campaign.updated_at || campaign.created_at || new Date().toISOString()
            });
          }
          
          // Si el status no es pendiente, agregar cambio de status
          if (campaign.status && campaign.status !== 'pendiente') {
            combinedHistory.push({
              id: `campaign-status-${campaign.id}`,
              action_type: 'status_changed',
              description: `Estado actualizado a: "${campaign.status}"`,
              user_name: campaign.agente || 'Sistema',
              field_changed: 'status',
              old_value: 'pendiente',
              new_value: campaign.status,
              created_at: campaign.updated_at || campaign.created_at || new Date().toISOString()
            });
          }
        }
        
        // Agregar historial real de la base de datos
        historyData.forEach(entry => {
          combinedHistory.push(entry);
        });
        
        // Ordenar por fecha descendente
        combinedHistory.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setHistory(combinedHistory);
      } else {
        console.error('Error al obtener historial:', response.status);
        setHistory([]);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionTypeLabel = (actionType) => {
    const labels = {
      'created': '🆕 Creado',
      'updated': '✏️ Actualizado',
      'asunto_defined': '📋 Asunto Definido',
      'acciones_added': '⚡ Acciones Tomadas',
      'observation_added': '👁️ Observación Agregada',
      'note_added': '📝 Nota Agregada',
      'status_changed': '🔄 Estado Cambiado'
    };
    return labels[actionType] || actionType;
  };

  const getActionTypeColor = (actionType) => {
    const colors = {
      'created': 'bg-green-100 text-green-800',
      'updated': 'bg-blue-100 text-blue-800',
      'asunto_defined': 'bg-indigo-100 text-indigo-800',
      'acciones_added': 'bg-emerald-100 text-emerald-800',
      'observation_added': 'bg-purple-100 text-purple-800',
      'note_added': 'bg-yellow-100 text-yellow-800',
      'status_changed': 'bg-orange-100 text-orange-800'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">📋 Historial de Campaña</h2>
              <p className="text-purple-100 mt-1">{campaignName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center animate-pulse">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">✅ Entrada agregada exitosamente al historial</span>
              </div>
            </div>
          )}

          {/* History Timeline */}
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-2 text-gray-600">Cargando historial...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📝</div>
              <p>No hay entradas en el historial</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div key={entry.id} className="relative">
                  {/* Timeline line */}
                  {index < history.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                  )}
                  
                  {/* Timeline entry */}
                  <div className="flex items-start space-x-4">
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-12 h-12 bg-white border-4 border-purple-200 rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    </div>
                    
                    {/* Entry content */}
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActionTypeColor(entry.action_type)}`}>
                          {getActionTypeLabel(entry.action_type)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      
                      <div className="text-gray-800 mb-2">
                        <p className="whitespace-pre-wrap">{entry.description}</p>
                      </div>
                      
                      {entry.user_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="mr-1">👤</span>
                          <span>Por: {entry.user_name}</span>
                        </div>
                      )}
                      
                      {/* Show field changes if available */}
                      {entry.field_changed && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <strong>Campo modificado:</strong> {entry.field_changed}
                          {entry.old_value && entry.new_value && (
                            <div className="mt-1">
                              <span className="text-red-600">Anterior: {entry.old_value}</span>
                              <span className="mx-2">→</span>
                              <span className="text-green-600">Nuevo: {entry.new_value}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignTrackingHistory;
